import React, { FC } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

import { choicesGet } from '@/selectors';
import State from '@/store/state';
import { usePropertyIdContext } from '@/kaas/PropertyIdContext';
import useKaasPropertyAssist from '@/kaas/useKaasPropertyAssist';
import { KAAS_COLOR } from '@/kaas/utils';
import { Tooltip } from '@mui/material';
import useAssistFeedback from '@/net/reactQuery/mutations/useAssistFeedback';
import classes from './ChoiceButton.module.css';
import { Choice, Property } from '../../../../store/types';

interface Props {
	choice: Choice;
	selected: boolean;
	onSelect(): void;
	property: Property;
}

const ChoiceRenderer: FC<Props> = ({ choice, selected, onSelect, property }): JSX.Element => {
	const { inspectionId } = useParams<Record<string, string>>();
	const assistFeedback = useAssistFeedback(inspectionId);
	const { suggestedAnswers, trackKaasAcceptedAnswer } = useKaasPropertyAssist(
		usePropertyIdContext(),
	);
	const isThisChoiceSuggested = suggestedAnswers && suggestedAnswers.answer.includes(choice.name);
	const color = isThisChoiceSuggested ? KAAS_COLOR : undefined;
	const boldness = isThisChoiceSuggested ? '900' : undefined;
	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
		<div
			data-testid="choice-button"
			className={selected ? classes.rootSelected : classes.root}
			role="button"
			onClick={() => {
				onSelect();
				if (isThisChoiceSuggested) {
					trackKaasAcceptedAnswer?.(choice.name);
					assistFeedback.mutate({
						feedbackType: 'ThumbsUp',
						files: [],
						questions: [property.id],
						retrieverType: 'FirstFeedback',
						traceId: suggestedAnswers.traceId,
					});
				}
			}}
		>
			{suggestedAnswers && suggestedAnswers.fileCitation !== '' ? (
				<Tooltip title={`Source ${suggestedAnswers.fileCitation}`} placement="bottom-start">
					<div className={classes.title} style={{ color, fontWeight: boldness }}>
						{choice.name}
					</div>
				</Tooltip>
			) : (
				<div className={classes.title} style={{ color, fontWeight: boldness }}>
					{choice.name}
				</div>
			)}
		</div>
	);
};

export default connect(
	(state: State, ownProps: { choiceId: Choice['id'] }): Pick<Props, 'choice'> => ({
		choice: choicesGet(state, ownProps.choiceId),
	}),
)(ChoiceRenderer);
